import { useEffect, useMemo, useState } from "react";
import {
    Alert, Button, Card, Empty, Input, InputNumber, Modal, Select, Space, Spin, Table, Tag, Tooltip, Typography,
} from "antd";
import {
    ControlOutlined, StopOutlined, EditOutlined, UndoOutlined, CheckCircleFilled, WarningFilled,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import SideBar from "../../components/SideBar";
import NavBar from "../../components/NavBar";
import Footer from "../../components/Footer";
import axiosInstance from "../../variables/api/axiosInstance";
import { rightsService } from "../../services/rightsService";

const { Title, Text } = Typography;
const PRIMARY = "#242166";

const dossierNum = (d) => d?.numeroSinistreM || d?.numSinistre || d?.codeId || "—";

/**
 * Module « Plafonds par dossier » des conseils juridiques (habilitation HSI096) :
 * pour les dossiers contentieux dont les plafonds réels sont déjà atteints
 * (audiences historiques hors PoolIns), on peut BLOQUER les frais de déplacement
 * ou FIXER un plafond individuel — prioritaire sur le plafond contractuel du conseil.
 */
const PlafondsConseilsJuridiques = () => {
    const canPlafonner = rightsService("HSI096");

    const [conseils, setConseils] = useState([]);
    const [conseil, setConseil] = useState(null);
    const [dossiers, setDossiers] = useState([]);
    const [statuses, setStatuses] = useState({});
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");

    // Modal « Fixer un plafond »
    const [plafondModal, setPlafondModal] = useState(null); // { dossier, plafond, motif }
    const [savingPlafond, setSavingPlafond] = useState(false);

    useEffect(() => {
        axiosInstance.get("/api/conseil-juridique")
            .then((res) => setConseils(Array.isArray(res.data) ? res.data : (res.data?.content ?? [])))
            .catch((e) => { console.error(e); toast.error("Erreur lors du chargement des conseils juridiques."); });
    }, []);

    const chargerStatuts = (c, codeIds) => {
        if (!c || !codeIds.length) { setStatuses({}); return; }
        axiosInstance.post(`/api/decaissement/conseils-juridiques/deplacements-status/${c.conseilJuridiqueId}`,
            JSON.stringify(codeIds), { headers: { "Content-Type": "application/json" } })
            .then((res) => {
                const map = {};
                (res.data || []).forEach((s) => { map[s.sinistreCodeId] = s; });
                setStatuses(map);
            })
            .catch((e) => { console.error(e); toast.error("Erreur lors du chargement des statuts de déplacement."); });
    };

    const handleSelectConseil = (id) => {
        const c = conseils.find((x) => x.conseilJuridiqueId === id) || null;
        setConseil(c);
        setDossiers([]);
        setStatuses({});
        setSearch("");
        if (!c) return;
        setLoading(true);
        axiosInstance.get(`/api/sinistre/conseil-juridique/${c.conseilJuridiqueId}`)
            .then((res) => {
                const data = Array.isArray(res.data)
                    ? res.data
                    : res.data?.content ?? res.data?.data ?? res.data?.items ?? [];
                setDossiers(data);
                chargerStatuts(c, data.map((d) => d.codeId).filter(Boolean));
            })
            .catch((e) => { console.error(e); toast.error("Erreur lors du chargement des dossiers."); })
            .finally(() => setLoading(false));
    };

    const recharger = () => chargerStatuts(conseil, dossiers.map((d) => d.codeId).filter(Boolean));

    const appliquerPlafond = (sinistreCodeId, plafondNombre, motif, messageSucces) => {
        return axiosInstance.put(`/api/conseil-juridique/${conseil.conseilJuridiqueId}/plafond-dossier`,
            JSON.stringify({ sinistreCodeId, plafondNombre, motif }),
            { headers: { "Content-Type": "application/json" } })
            .then(() => { toast.success(messageSucces); recharger(); })
            .catch((e) => {
                console.error(e);
                toast.error(e?.response?.data?.message || "Erreur lors de l'enregistrement du plafond.");
            });
    };

    // « Bloquer » = plafond individuel fixé au nombre déjà payé → tout nouveau paiement refusé.
    const bloquer = (dossier) => {
        const st = statuses[dossier.codeId];
        const payes = st?.deplacementsPayes ?? 0;
        Modal.confirm({
            title: `Bloquer les frais de déplacement du dossier ${dossierNum(dossier)} ?`,
            content: (
                <div>
                    <p>Le plafond individuel sera fixé à <b>{payes}</b> (nombre de paiements déjà enregistrés) :
                        tout nouveau paiement de déplacement sera refusé.</p>
                    <Input.TextArea id="motif-blocage" rows={2} placeholder="Motif (obligatoire) — ex. audiences historiques hors PoolIns" />
                </div>
            ),
            okText: "Bloquer", okButtonProps: { danger: true }, cancelText: "Annuler",
            onOk: () => {
                const motif = document.getElementById("motif-blocage")?.value?.trim();
                if (!motif) { toast.warning("Le motif est obligatoire."); return Promise.reject(); }
                return appliquerPlafond(dossier.codeId, payes, motif, "Dossier bloqué (plafond atteint d'office).");
            },
        });
    };

    const lever = (dossier) => {
        Modal.confirm({
            title: `Lever le plafond individuel du dossier ${dossierNum(dossier)} ?`,
            content: "Le dossier reviendra aux conditions du contrat du conseil.",
            okText: "Lever", cancelText: "Annuler",
            onOk: () => appliquerPlafond(dossier.codeId, null, null, "Plafond individuel levé."),
        });
    };

    const validerPlafondModal = () => {
        if (!plafondModal) return;
        const { dossier, plafond, motif } = plafondModal;
        if (plafond == null || plafond < 0) { toast.warning("Saisissez un plafond (nombre de paiements max)."); return; }
        if (!motif?.trim()) { toast.warning("Le motif est obligatoire."); return; }
        setSavingPlafond(true);
        appliquerPlafond(dossier.codeId, plafond, motif.trim(), "Plafond individuel enregistré.")
            .finally(() => { setSavingPlafond(false); setPlafondModal(null); });
    };

    const dossiersFiltres = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return dossiers;
        return dossiers.filter((d) => dossierNum(d).toLowerCase().includes(q)
            || (statuses[d.codeId]?.juridiction || "").toLowerCase().includes(q));
    }, [dossiers, statuses, search]);

    const columns = [
        {
            title: "Dossier", key: "dossier",
            render: (_, d) => <Text strong style={{ fontFamily: "monospace" }}>{dossierNum(d)}</Text>,
        },
        {
            title: "Juridiction", key: "juridiction",
            render: (_, d) => {
                const st = statuses[d.codeId];
                if (!st) return "—";
                return st.juridiction
                    ? <span>{st.juridiction}{st.juridictionExclue && <Tag color="red" style={{ marginLeft: 6 }}>Exclue</Tag>}</span>
                    : <Text type="secondary">Non renseignée{st.juridictionExclue ? " (bloqué)" : ""}</Text>;
            },
        },
        {
            title: "Déplacements", key: "deplacements", align: "center",
            render: (_, d) => {
                const st = statuses[d.codeId];
                if (!st) return <Spin size="small" />;
                const label = st.plafondEffectif != null
                    ? `${st.deplacementsPayes} / ${st.plafondEffectif}`
                    : `${st.deplacementsPayes} (illimité)`;
                return st.bloque
                    ? <Tooltip title={st.motifBlocage}><Tag color="red" icon={<WarningFilled />}>{label} — bloqué</Tag></Tooltip>
                    : <Tag color="green" icon={<CheckCircleFilled />}>{label}</Tag>;
            },
        },
        {
            title: "Plafond individuel", key: "plafondDossier",
            render: (_, d) => {
                const st = statuses[d.codeId];
                if (!st?.plafondDossier && st?.plafondDossier !== 0) return <Text type="secondary">—</Text>;
                return (
                    <Tooltip title={st.motifPlafondDossier}>
                        <Tag color="purple">{st.plafondDossier} max</Tag>
                    </Tooltip>
                );
            },
        },
        {
            title: "PV constat", key: "pv", align: "center",
            render: (_, d) => statuses[d.codeId]?.fraisPvConstatPaye
                ? <Tag color="blue">Payé</Tag> : <Text type="secondary">—</Text>,
        },
        {
            title: "Frais de gestion", key: "gestion", align: "center",
            render: (_, d) => statuses[d.codeId]?.fraisGestionPaye
                ? <Tag color="blue">Payé</Tag> : <Text type="secondary">—</Text>,
        },
        ...(canPlafonner ? [{
            title: "Actions", key: "actions", align: "center", width: 160,
            render: (_, d) => {
                const st = statuses[d.codeId];
                return (
                    <Space>
                        <Tooltip title="Bloquer les déplacements (plafond atteint d'office)">
                            <Button size="small" danger icon={<StopOutlined />} onClick={() => bloquer(d)} />
                        </Tooltip>
                        <Tooltip title="Fixer un plafond individuel">
                            <Button size="small" icon={<EditOutlined />}
                                onClick={() => setPlafondModal({ dossier: d, plafond: st?.plafondDossier ?? st?.plafondEffectif ?? null, motif: st?.motifPlafondDossier || "" })} />
                        </Tooltip>
                        {(st?.plafondDossier != null) && (
                            <Tooltip title="Lever le plafond individuel (retour au contrat)">
                                <Button size="small" icon={<UndoOutlined />} onClick={() => lever(d)} />
                            </Tooltip>
                        )}
                    </Space>
                );
            },
        }] : []),
    ];

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"SINISTRES"} />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">
                            <div style={{ marginBottom: 18 }}>
                                <Title level={4} style={{ margin: 0, color: "#111827" }}>
                                    <ControlOutlined style={{ marginRight: 8, color: PRIMARY }} />Plafonds par dossier — conseils juridiques
                                </Title>
                                <Text type="secondary">
                                    Bloquer ou plafonner individuellement les frais de déplacement d'un dossier
                                    (prioritaire sur le plafond contractuel du conseil).
                                </Text>
                            </div>

                            {!canPlafonner && (
                                <Alert type="warning" showIcon style={{ marginBottom: 14 }}
                                    message="Consultation seule : l'habilitation HSI096 est requise pour bloquer ou plafonner un dossier." />
                            )}

                            <Card bordered={false} style={{ borderRadius: 12, marginBottom: 14, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }} bodyStyle={{ padding: 12 }}>
                                <Space wrap>
                                    <Select
                                        showSearch allowClear optionFilterProp="label"
                                        placeholder="Sélectionner un conseil juridique…"
                                        style={{ width: 320 }}
                                        value={conseil?.conseilJuridiqueId}
                                        onChange={handleSelectConseil}
                                        options={conseils.map((c) => ({ value: c.conseilJuridiqueId, label: c.nom }))}
                                    />
                                    {conseil && (
                                        <Text type="secondary">
                                            Contrat : {conseil.plafondDeplacementsParDossier != null
                                                ? `${conseil.plafondDeplacementsParDossier} déplacements max / dossier`
                                                : "déplacements illimités"}
                                            {conseil.juridictionsExcluesDeplacement
                                                ? ` · juridictions exclues : ${conseil.juridictionsExcluesDeplacement.split(";").join(", ")}`
                                                : ""}
                                        </Text>
                                    )}
                                    {conseil && (
                                        <Input.Search allowClear placeholder="N° dossier, juridiction…" style={{ width: 240 }}
                                            onSearch={setSearch} onChange={(e) => { if (!e.target.value) setSearch(""); }} />
                                    )}
                                </Space>
                            </Card>

                            <Card bordered={false} style={{ borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                                <Spin spinning={loading}>
                                    <Table
                                        rowKey="codeId"
                                        columns={columns}
                                        dataSource={dossiersFiltres}
                                        scroll={{ x: "max-content" }}
                                        pagination={{ pageSize: 15, showSizeChanger: true, showTotal: (t) => `${t} dossier(s)` }}
                                        locale={{
                                            emptyText: <Empty description={conseil
                                                ? "Aucun dossier affecté à ce conseil"
                                                : "Sélectionnez un conseil juridique"} />,
                                        }}
                                    />
                                </Spin>
                            </Card>
                        </div>
                        <Footer />
                        <div className="content-backdrop fade"></div>
                    </div>
                </div>
            </div>
            <div className="layout-overlay layout-menu-toggle"></div>
            <div className="drag-target"></div>

            <Modal
                open={!!plafondModal}
                title={plafondModal ? `Plafond individuel — dossier ${dossierNum(plafondModal.dossier)}` : ""}
                onCancel={() => setPlafondModal(null)}
                onOk={validerPlafondModal}
                confirmLoading={savingPlafond}
                okText="Enregistrer" cancelText="Annuler"
                okButtonProps={{ style: { background: PRIMARY, border: "none" } }}
                destroyOnClose
            >
                {plafondModal && (
                    <Space direction="vertical" style={{ width: "100%" }} size="middle">
                        <div>
                            <Text>Nombre max de paiements de frais de déplacement pour CE dossier :</Text>
                            <InputNumber
                                min={0} style={{ width: "100%", marginTop: 6 }}
                                value={plafondModal.plafond}
                                onChange={(v) => setPlafondModal((p) => ({ ...p, plafond: v }))}
                            />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Déjà payés : {statuses[plafondModal.dossier.codeId]?.deplacementsPayes ?? 0}.
                                Ce plafond est prioritaire sur celui du contrat.
                            </Text>
                        </div>
                        <Input.TextArea
                            rows={2} placeholder="Motif (obligatoire)"
                            value={plafondModal.motif}
                            onChange={(e) => setPlafondModal((p) => ({ ...p, motif: e.target.value }))}
                        />
                    </Space>
                )}
            </Modal>
        </div>
    );
};

export default PlafondsConseilsJuridiques;
